import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { add } from 'date-fns';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import { getDiffInDays } from '../utils';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackUnableToFileBddDisplayed,
  trackExternalLinkClick,
} from '../../utils/tracking/features/screenerTracking';
import { formatDate } from '../../../shared/utils/dates';
import { BDD_INFO_URL } from '../../constants';
import { SHA_PART_A_PDF_URL } from '../../../shared/constants/entryConstants';
import pageNames from './pageNames';

// Veterans become eligible to file once they're within 180 days of separating,
// so the wait is however many days remain before crossing that threshold.
const BDD_MAX_DAYS = 180;

/**
 * Unable to file BDD — shown when an active-duty service member's separation
 * date is more than 180 days away, which is too early to file. Terminal page
 * with no Continue; the veteran is told when to come back.
 * @param {Object} data - subtask data
 * @returns {JSX}
 */
const UnableToFileBddPage = ({ data = {} }) => {
  const { radDate } = data;
  const { addStep, getHistory } = usePathHistory();

  const daysUntilEligible = getDiffInDays(radDate) + 1 - BDD_MAX_DAYS;
  const daysLabel = daysUntilEligible === 1 ? 'day' : 'days';
  const dateEligible = formatDate(add(new Date(), { days: daysUntilEligible }));

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.unableToFileBdd,
      pathHistoryContext: { addStep, getHistory },
    });
    trackUnableToFileBddDisplayed({
      reason: 'too-early',
      daysRemaining: getDiffInDays(radDate) + 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id={pageNames.unableToFileBdd}>
      <SubTaskContainerHeader title="File your disability compensation claim later" />
      <va-alert status="info" class="vads-u-margin-bottom--3">
        <h3 slot="headline">
          You must submit a Separation Health Assessment with your claim
        </h3>
        <p className="vads-u-margin-top--0">
          To be considered for the BDD program, you must submit a Separation
          Health Assessment (self-assessment, also called “Part A”) with your
          disability compensation claim.
        </p>
        <va-link
          external
          href={SHA_PART_A_PDF_URL}
          text="Download your Separation Health Assessment"
          onClick={() =>
            trackExternalLinkClick({
              linkLabel: 'Download your Separation Health Assessment',
              pageName: pageNames.unableToFileBdd,
              pathHistoryData: getHistory(),
            })
          }
        />
      </va-alert>
      <p>
        Based on your separation date, you’ll be eligible to file a
        pre-discharge claim online in <strong>{daysUntilEligible}</strong>{' '}
        {daysLabel} (<strong>{dateEligible}</strong>).
      </p>
      <p>
        The pre-discharge claim is also known as the “Benefits Delivery at
        Discharge (BDD)” program and is available through VA Form 21-526EZ.
      </p>
      <va-link
        external
        href={BDD_INFO_URL}
        text="Learn more about the pre-discharge claim"
        onClick={() =>
          trackExternalLinkClick({
            linkLabel: 'Learn more about the pre-discharge claim',
            pageName: pageNames.unableToFileBdd,
            pathHistoryData: getHistory(),
          })
        }
      />
    </div>
  );
};

UnableToFileBddPage.propTypes = {
  data: PropTypes.shape({}),
};

export default {
  name: pageNames.unableToFileBdd,
  component: UnableToFileBddPage,
  back: pageNames.releaseDate,
  // Terminal page — the veteran can't file yet, so there's nothing to continue
  // to. Returning null hides the Continue button.
  next: null,
};
